import type { ChartOptions } from 'chart.js';
import { formatUsd } from '@dapp/multi-lib/lib/formatters';

interface ChatParams {
  title: string;
}

export interface AssetDistributionEntry {
  label: string;
  value: number;
}

const CUMULATIVE_THRESHOLD = 0.9;
const MIN_MAJOR_ENTRIES = 2;
const MAX_UNGROUPED_ENTRIES = 5;

export const sumAssetDistributionEntries = (
  entries: AssetDistributionEntry[]
): AssetDistributionEntry[] =>
  Object.values(
    entries.reduce(
      (acc, entry) => {
        acc[entry.label] = {
          label: entry.label,
          value: (acc[entry.label]?.value ?? 0) + entry.value,
        };
        return acc;
      },
      {} as Record<string, AssetDistributionEntry>
    )
  );

export const groupAssetDistributionEntries = (
  entries: AssetDistributionEntry[]
): AssetDistributionEntry[] => {
  if (entries.length <= MAX_UNGROUPED_ENTRIES) {
    return entries;
  }

  const total = entries.reduce((sum, item) => sum + item.value, 0);
  let cumulative = 0;
  let cutIndex = entries.length;

  for (let i = 0; i < entries.length; i++) {
    cumulative += total > 0 ? entries[i].value / total : 0;
    if (cumulative >= CUMULATIVE_THRESHOLD && i + 1 >= MIN_MAJOR_ENTRIES) {
      cutIndex = i + 1;
      break;
    }
  }

  const majorEntries = entries.slice(0, cutIndex);
  const otherEntries = entries.slice(cutIndex);
  const otherValue = otherEntries.reduce((sum, item) => sum + item.value, 0);

  return otherValue > 0 && otherEntries.length > 1
    ? [...majorEntries, { label: 'Others', value: otherValue }]
    : entries;
};

export const buildChartOptions = (params: ChatParams): ChartOptions<'pie'> => {
  const { title } = params;

  return {
    responsive: true,
    borderColor: '#1b1d1e',
    layout: {
      padding: {
        bottom: 10,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: function () {
            return '';
          },
          label: function (context) {
            const value = context.parsed;
            const sum = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / sum) * 100).toFixed(2);

            return `${context.label}: $${formatUsd(value)} (${percentage}%)`;
          },
        },
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: title,
        position: 'top' as const,
        color: '#ffffff',
        font: {
          size: 16,
        },
      },
    },
  };
};

const TOKEN_COLORS: Record<string, string> = {
  ETH: '#627EEA',
  WETH: '#8AA2F8',
  BNB: '#F3BA2F',
  USDC: '#2775CA',
  EURC: '#16A34A',
  BTC: '#F7931A',
  cbBTC: '#FF9F1C',
};

function hashString(input: string): number {
  let hash = 0;

  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }

  return Math.abs(hash);
}

function hslColor(h: number, s = 68, l = 52): string {
  return `hsl(${h}, ${s}%, ${l}%)`;
}

export function getTokenColor(symbol: string, index?: number): string {
  const predefined = TOKEN_COLORS[symbol];
  if (predefined) return predefined;

  const baseHue = hashString(symbol) % 360;
  const hue = index === undefined ? baseHue : (baseHue + index * 37) % 360;

  return hslColor(hue);
}
